import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, authStatus, AUTH_STATE } = useAuth();
  const isAuthenticated = authStatus === AUTH_STATE?.AUTHENTICATED && !!userProfile;

  // إظهار القائمة على جميع الأجهزة (جوال، تابلت، سطح المكتب) — على سطح المكتب بعرض محدود وفي المنتصف

  // اجعل الدور الافتراضي "عميل" إذا لم يكن محدداً، حتى تظهر القائمة دائماً
  const role = userProfile?.role === 'photographer' ? 'photographer' : 'customer';
  const isCustomer = role === 'customer';
  const isPhotographer = role === 'photographer';

  // Customer bottom nav: الرئيسية واستكشاف بدون تسجيل — حجوزاتي ولوحة التحكم وحسابي تتطلب تسجيل دخول
  const customerItems = [
    {
      key: 'home',
      label: 'الرئيسية',
      icon: 'Home',
      path: '/',
      match: ['/'],
    },
    {
      key: 'browse',
      label: 'استكشاف',
      icon: 'Search',
      path: '/browse-photographers',
      match: ['/browse-photographers', '/photographer-results'],
    },
    {
      key: 'bookings',
      label: 'حجوزاتي',
      icon: 'Calendar',
      path: '/customer-dashboard/bookings',
      match: ['/customer-dashboard/bookings', '/bookings-management'],
      requiresAuth: true,
      guestPath: '/customer-login',
    },
    {
      key: 'dashboard',
      label: 'لوحة التحكم',
      icon: 'LayoutDashboard',
      path: '/customer-dashboard',
      match: ['/customer-dashboard'],
      exact: true,
      requiresAuth: true,
      guestPath: '/customer-login',
    },
    {
      key: 'profile',
      label: 'حسابي',
      icon: 'User',
      path: '/customer-dashboard/account',
      match: ['/customer-dashboard/account', '/customer-dashboard/profile', '/customer-dashboard/delete-account'],
      requiresAuth: true,
      guestPath: '/account',
    },
  ];

  // Photographer bottom navigation items (لوحة التحكم للمصور)
  const photographerItems = [
    {
      key: 'dashboard',
      label: 'الرئيسية',
      icon: 'LayoutDashboard',
      path: '/photographer-dashboard',
      match: ['/photographer-dashboard'],
      exact: true, // لوحة التحكم فقط - لا تطابق الصفحات الفرعية مثل /bookings
    },
    {
      key: 'requests',
      label: 'الطلبات',
      icon: 'CalendarCheck',
      path: '/photographer-dashboard/bookings',
      match: ['/photographer-dashboard/bookings'],
    },
    {
      key: 'payments',
      label: 'المدفوعات',
      icon: 'Wallet',
      path: '/photographer-dashboard/payments',
      match: ['/photographer-dashboard/payments'],
    },
    {
      key: 'portfolio',
      label: 'أعمالي',
      icon: 'Image',
      path: '/photographer-dashboard/portfolio-management',
      match: ['/photographer-dashboard/portfolio', '/photographer-dashboard/portfolio-management'],
    },
    {
      key: 'profile',
      label: 'حسابي',
      icon: 'UserCog',
      path: '/photographer-dashboard/profile',
      match: ['/photographer-dashboard/profile'],
    },
  ];

  const items = isCustomer ? customerItems : photographerItems;

  const isItemActive = (item) => {
    const { pathname } = location;
    return item.match.some((segment) => {
      if (segment === '/') return pathname === '/';
      if (item.exact) return pathname === segment || pathname === segment + '/';
      return pathname.startsWith(segment);
    });
  };

  const handleNavigate = (e, item, isActive) => {
    e.preventDefault();
    if (isActive) return;
    const path = item.path;
    if (location.pathname === path) return;
    if (isCustomer && item.requiresAuth && !isAuthenticated && item.guestPath) {
      navigate(item.guestPath);
      return;
    }
    navigate(path);
  };

  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setHidden(
        document.body.classList.contains('ticket-modal-open') ||
        document.body.classList.contains('map-modal-open') ||
        document.body.classList.contains('album-viewer-open')
      );
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (hidden) return null;

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-[1300] flex justify-center px-2 sm:px-4"
      aria-label="التنقل السفلي"
    >
      <div
        className={cn(
          'w-full mx-auto bg-white/95 backdrop-blur border-t border-slate-200 rounded-t-2xl shadow-lg',
          'max-w-3xl',
          'lg:max-w-xl lg:rounded-2xl lg:border lg:border-slate-200'
        )}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        }}
      >
        <div className="flex items-stretch justify-around h-[72px] px-1">
          {items.map((item) => {
            const active = isItemActive(item);
            return (
              <button
                key={item.key}
                type="button"
                onClick={(e) => handleNavigate(e, item, active)}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 gap-0.5 min-h-[48px] min-w-[48px] py-2 text-[11px] font-medium transition-colors cursor-pointer select-none',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
                  'touch-manipulation active:scale-[0.97]',
                  active ? 'text-primary' : 'text-slate-500 hover:text-slate-800'
                )}
                aria-label={item.label}
              >
                <Icon
                  name={item.icon}
                  size={22}
                  className={active ? 'text-primary' : 'text-slate-500'}
                />
                <span className="truncate max-w-[64px]">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MobileBottomNav;

